import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BrandLogo } from '../shared/BrandLogo'
import { portalLinks } from './portal-links'
import { FOOTER_RISK_WARNING, RISK_LINKS } from './risk-copy'
import './web-test-footer.css'
import { trackClientLogin, trackOpenAccount, trackRiskDisclosure } from '../analytics/track'

const PRODUCT = [
  { label: 'How It Works', href: '/how-perfect-predict-works' },
  { label: 'Strategies', href: '/prediction-strategies' },
  { label: 'Gold', href: '/gold-prediction-trading' },
  { label: 'Silver', href: '/silver-prediction-trading' },
  { label: 'Platform', href: '/mt5-trading-infrastructure' },
  { label: 'Funding', href: '/funding-and-withdrawals' },
  { label: 'Demo', href: '/demo-trading-account' },
  { label: 'FAQ', href: '/#faq' },
] as const

const COMPANY = [
  { label: 'About', href: '/about' },
  { label: 'Blog', href: '/blog' },
  { label: 'Support', href: '/support' },
  { label: 'Contact', href: '/contact' },
] as const

const LEGAL = [
  { label: 'Terms', href: '/terms' },
  { label: 'Privacy', href: '/privacy' },
  { label: 'Risk Disclosure', href: '/risk-disclosure' },
  { label: 'Cookies', href: '/cookies' },
] as const

export function WebTestFooter() {
  const rootRef = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true)
          io.disconnect()
        }
      },
      { threshold: 0.08 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const [before, after] = FOOTER_RISK_WARNING.split('Risk Disclosure')

  return (
    <footer
      ref={rootRef}
      id="site-footer"
      className={`wft-footer${visible ? ' is-visible' : ''}`}
    >
      <div className="wft-inner">
        <div className="wft-top pp-enter" style={{ ['--pp-i' as string]: 0 }}>
          <div className="wft-brand">
            <BrandLogo tone="light" href="/" />
            <p className="wft-brand__tag">Structured Prediction Trading</p>
          </div>

          <nav className="wft-nav" aria-label="Footer">
            <div className="wft-nav__col">
              <p className="wft-nav__title">Product</p>
              <ul>
                {PRODUCT.map((item) => (
                  <li key={item.label}>
                    {item.href.startsWith('/#') || item.href.startsWith('#') ? (
                      <a href={item.href}>{item.label}</a>
                    ) : (
                      <Link to={item.href}>{item.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="wft-nav__col">
              <p className="wft-nav__title">Company</p>
              <ul>
                {COMPANY.map((item) => (
                  <li key={item.label}>
                    <Link to={item.href}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="wft-nav__col">
              <p className="wft-nav__title">Legal</p>
              <ul>
                {LEGAL.map((item) => (
                  <li key={item.label}>
                    <Link to={item.href}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          <div className="wft-actions">
            <a
              className="wt-btn wt-btn--primary wt-btn--hero"
              href={portalLinks.register}
              onClick={() => trackOpenAccount('footer')}
            >
              Open Live Account
            </a>
            <a
              className="wft-actions__link"
              href={portalLinks.login}
              onClick={() => trackClientLogin('footer')}
            >
              Client Portal Login
            </a>
          </div>
        </div>

        <div
          id="footer-risk-warning"
          className="wft-risk pp-enter"
          style={{ ['--pp-i' as string]: 1 }}
        >
          <p className="wft-risk__title">Risk Warning</p>
          <p className="wft-risk__copy">
            {before}
            <Link to={RISK_LINKS.disclosure} onClick={() => trackRiskDisclosure('footer')}>
              Risk Disclosure
            </Link>
            {after}
          </p>
        </div>

        <div className="wft-divider" aria-hidden="true" />

        <div className="wft-bottom pp-enter" style={{ ['--pp-i' as string]: 2 }}>
          <p className="wft-bottom__left">© 2026 Perfect Predict</p>
        </div>
      </div>
    </footer>
  )
}
